import { useState, useEffect } from 'react';
import { invoicesAPI, quotesAPI } from '../utils/api';
import { logger } from '@/utils/logger';

interface NavigationBadges {
  unpaidInvoices: number;
  overdueInvoices: number;
  draftQuotes: number;
  pendingReminders: number;
}

export const useNavigationBadges = () => {
  const [badges, setBadges] = useState<NavigationBadges>({
    unpaidInvoices: 0,
    overdueInvoices: 0,
    draftQuotes: 0,
    pendingReminders: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchBadges = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [invoicesData, quotesData] = await Promise.all([
        invoicesAPI.getAll(),
        quotesAPI.getAll(),
      ]);

      // API returns array directly
      const invoices = Array.isArray(invoicesData) ? invoicesData : ((invoicesData as any)?.invoices || []);
      const quotes = Array.isArray(quotesData) ? quotesData : ((quotesData as any)?.quotes || []);

      const unpaidCount = invoices.filter(
        (inv: any) => inv.status === 'SENT' || inv.status === 'OVERDUE'
      ).length || 0;

      const overdueCount = invoices.filter(
        (inv: any) => inv.status === 'OVERDUE'
      ).length || 0;

      const draftQuotesCount = quotes.filter(
        (quote: any) => quote.status === 'DRAFT'
      ).length || 0;

      setBadges({
        unpaidInvoices: unpaidCount,
        overdueInvoices: overdueCount,
        draftQuotes: draftQuotesCount,
        pendingReminders: 0,
      });
    } catch (err) {
      logger.error('Error fetching navigation badges:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBadges();

    // Auto-refresh every 60 seconds
    const interval = setInterval(() => {
      fetchBadges();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  return {
    badges,
    isLoading,
    error,
  };
};
